import { useRef, ReactNode } from 'react'
import { motion, useInView } from 'framer-motion'
import AnimatedBackground from './AnimatedBackground'
import {
  SiHtml5,
  SiCss3,
  SiSass,
  SiJavascript,
  SiReact,
  SiNextdotjs,
  SiTailwindcss,
  SiNodedotjs,
  SiExpress,
  SiMysql,
  SiMongodb,
  SiFirebase,
  SiC,
  SiCplusplus,
  SiPython,
  SiTypescript,
  SiGit,
  SiGithub,
  SiPostman,
  SiVercel,
  SiNetlify,
  
} from 'react-icons/si'
import { TbBrandVscode, TbTopologyStar3 } from 'react-icons/tb'
import { SiDocker, SiKubernetes } from 'react-icons/si'

// Technology Logo Component
function TechLogo({ name, icon, color }: { name: string, icon: ReactNode, color: string }) {
  return (
    <motion.div
      className="flex flex-col items-center space-y-2 p-3 rounded-lg hover:bg-gray-800/30 transition-all duration-300 group"
      whileHover={{ scale: 1.05 }}
    >
      <div 
        className="w-12 h-12 flex items-center justify-center rounded-lg text-2xl"
        style={{ 
          backgroundColor: `${color}20`,
          color: color,
          border: `1px solid ${color}40`
        }}
      >
        {icon}
      </div>
      <span className="text-xs font-mono text-gray-300 group-hover:text-white transition-colors">
        {name}
      </span>
    </motion.div>
  )
}

export default function Skills() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const skillCategories = [
    {
      title: "Frontend",
      technologies: [
        { name: "HTML", icon: <SiHtml5 size={24} />, color: "#E34F26" },
        { name: "CSS", icon: <SiCss3 size={24} />, color: "#1572B6" },
        { name: "SASS", icon: <SiSass size={24} />, color: "#CF649A" },
        { name: "JavaScript", icon: <SiJavascript size={24} />, color: "#F7DF1E" },
        { name: "React", icon: <SiReact size={24} />, color: "#61DAFB" },
        { name: "Next.js", icon: <SiNextdotjs size={24} />, color: "#ffffff" },
        { name: "Tailwind", icon: <SiTailwindcss size={24} />, color: "#06B6D4" },
       
      ]
    },
    {
      title: "Backend",
      technologies: [
        { name: "Node.js", icon: <SiNodedotjs size={24} />, color: "#339933" },
        { name: "Express", icon: <SiExpress size={24} />, color: "#FFFFFF" },
        { name: "MySQL", icon: <SiMysql size={24} />, color: "#4479A1" },
        { name: "MongoDB", icon: <SiMongodb size={24} />, color: "#47A248" },
        { name: "Firebase", icon: <SiFirebase size={24} />, color: "#FFCA28" },
        { name: "Docker", icon: <SiDocker size={24} />, color: "#2496ED" },
        { name: "Kubernetes", icon: <SiKubernetes size={24} />, color: "#326CE5" },
        { name: "Microservices", icon: <TbTopologyStar3 size={24} />, color: "#22c55e" }
      ]
    },
    {
      title: "Languages",
      technologies: [
        { name: "C", icon: <SiC size={24} />, color: "#A8B9CC" },
        { name: "C++", icon: <SiCplusplus size={24} />, color: "#00599C" },
        { name: "Python", icon: <SiPython size={24} />, color: "#3776AB" },
        { name: "JavaScript", icon: <SiJavascript size={24} />, color: "#F7DF1E" },
        { name: "TypeScript", icon: <SiTypescript size={24} />, color: "#3178C6" }
      ]
    },
    {
      title: "Tools",
      technologies: [
        { name: "Git", icon: <SiGit size={24} />, color: "#F05032" },
        { name: "GitHub", icon: <SiGithub size={24} />, color: "#ffffff" },
        { name: "VS Code", icon: <TbBrandVscode size={24} />, color: "#007ACC" },
        { name: "Postman", icon: <SiPostman size={24} />, color: "#FF6C37" },
        { name: "Vercel", icon: <SiVercel size={24} />, color: "#ffffff" },
        { name: "Netlify", icon: <SiNetlify size={24} />, color: "#00C7B7" },
      ]
    }
  ]

  return (
    <section id="skills" ref={ref} className="relative py-20 min-h-screen bg-black overflow-hidden">
      {/* Animated Background */}
      <AnimatedBackground variant="grid" intensity="medium" />
      <AnimatedBackground variant="gradient" intensity="low" />
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-display font-black mb-4">
            <span className="glitch-text text-neon-pink neon-glow" data-text="SKILLS">
              SKILLS
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-neon-pink to-neon-purple mx-auto mb-8" />
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Technical expertise across multiple domains of software development and innovation
          </p>
        </motion.div>

        {/* Skills Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {skillCategories.map((category, categoryIndex) => (
        <motion.div
              key={category.title}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={isInView ? { 
                  opacity: 1, 
                  y: 0, 
                  scale: 1 
                } : {}}
                transition={{ 
                  duration: 0.8, 
                delay: categoryIndex * 0.2,
                  ease: "easeOut"
                }}
              className="bg-dark-800/30 backdrop-blur-md rounded-xl border border-gray-700/50 p-6 hover:border-neon-green/50 hover:shadow-lg hover:shadow-neon-green/20 transition-all duration-300"
                  whileHover={{ 
                    scale: 1.02,
                boxShadow: "0 0 30px rgba(147, 51, 234, 0.3)"
                  }}
                >
                  {/* Card Header */}
                          <motion.h3 
                className="text-2xl font-display font-bold text-white mb-6 text-center"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.8, delay: categoryIndex * 0.2 + 0.3 }}
                          >
                            {category.title}
                          </motion.h3>

              {/* Technologies Grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {category.technologies.map((tech, techIndex) => (
                      <motion.div
                    key={tech.name}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={isInView ? { 
                      opacity: 1, 
                      scale: 1 
                    } : {}}
                    transition={{ 
                      duration: 0.5, 
                      delay: categoryIndex * 0.2 + techIndex * 0.05 + 0.5,
                      ease: "easeOut"
                    }}
                  >
                    <TechLogo 
                      name={tech.name} 
                      icon={tech.icon} 
                      color={tech.color} 
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Skills */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <h3 className="text-2xl font-display font-bold text-neon-yellow mb-8">
            ADDITIONAL EXPERTISE
          </h3>
          
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {[
              'Problem Solving', 'Algorithm Design', 'System Architecture', 
              'Database Design', 'API Development', 'Version Control',
              'Testing & Debugging', 'Performance Optimization', 'Code Review',
              'Agile Methodologies', 'Project Management', 'Team Collaboration'
            ].map((skill, index) => (
              <motion.span
                key={skill}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 1 + index * 0.05 }}
                className="px-4 py-2 bg-dark-800/50 border border-neon-yellow/30 rounded-lg text-neon-yellow font-mono text-sm hover:bg-neon-yellow/10 transition-all duration-300"
              >
                {skill}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}